/**
 * Cookie consent: single source of truth.
 *
 * Stores consent in localStorage under `elh_cookie_consent_v1`:
 *   {
 *     consent_id: "c_xxxxxxxxxxx",   // stable ID for this browser
 *     version: "2026.02.1",          // policy version at time of consent
 *     decided_at: ISO datetime,
 *     categories: {
 *       necessary: true,             // always true (locked)
 *       analytics: bool,
 *       preferences: bool,
 *       marketing: bool,
 *     },
 *   }
 *
 * Subscribers (e.g. the banner, the footer button) listen via window event
 * "elh-consent-change" to react to updates.
 */
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "elh_cookie_consent_v1";

export const CURRENT_POLICY_VERSION = "2026.02.1";

export const COOKIE_CATEGORIES = [
    {
        key: "necessary",
        label: "Strictly necessary",
        locked: true,
        legal_basis: "Art. 6(1)(b) GDPR, performance of a contract, and Art. 6(1)(f), legitimate interests in keeping the service secure and functional.",
        description:
            "Required for authentication, session continuity, security (CSRF/XSRF protection), and to remember UI state during your visit. Cannot be turned off because the site will not function without them.",
        examples: [
            "Authentication token (JWT) stored in localStorage when you sign in",
            "Active conversation/session identifiers used by Research Assistant",
            "Workspace progress saved in your browser (AI Act / DORA / NIS2 checklists)",
            "Cookie-consent record itself",
        ],
    },
    {
        key: "preferences",
        label: "Preferences",
        locked: false,
        legal_basis: "Art. 6(1)(a) GDPR, your consent.",
        description:
            "Remember UI preferences and recently-viewed items so the product feels personal across visits. Disabling these will not break the product, but it will reset your selections each time.",
        examples: [
            "Last research mode and depth selector position",
            "Recently-viewed bulletins / cases",
            "Tab selection on workspaces (AI Act / GDPR / DORA / NIS2)",
        ],
    },
    {
        key: "analytics",
        label: "Analytics",
        locked: false,
        legal_basis: "Art. 6(1)(a) GDPR, your consent.",
        description:
            "Anonymous, aggregated metrics on how the product is used so we can improve it. We do not currently load any third-party analytics scripts; this toggle reserves your choice for when we do.",
        examples: [
            "Page-view counts and feature-usage metrics (aggregated, no profiling)",
            "Crash and error reporting (no payloads from your prompts or documents)",
        ],
    },
    {
        key: "marketing",
        label: "Marketing",
        locked: false,
        legal_basis: "Art. 6(1)(a) GDPR, your consent.",
        description:
            "Allow us to measure the effectiveness of marketing campaigns. We do not currently set marketing cookies; this toggle reserves your choice for when we do.",
        examples: [
            "Conversion measurement on the landing-page Try-it-free widget",
            "Attribution of sign-ups to referral sources",
        ],
    },
];

function _genId() {
    return "c_" + Math.random().toString(36).slice(2, 14) + Date.now().toString(36);
}

export function getConsent() {
    if (typeof window === "undefined") return null;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Re-prompt if the policy version has changed
        if (parsed.version !== CURRENT_POLICY_VERSION) return null;
        // Defensive: ensure necessary is true
        parsed.categories = { ...parsed.categories, necessary: true };
        return parsed;
    } catch {
        return null;
    }
}

export function isCategoryEnabled(category) {
    if (category === "necessary") return true;
    const c = getConsent();
    if (!c) return false;
    return !!c.categories?.[category];
}

export function withdrawConsent() {
    try {
        const existing = getConsent();
        if (existing) {
            void sendLog({
                consent_id: existing.consent_id,
                version: existing.version,
                action: "withdraw",
                categories: { necessary: true, preferences: false, analytics: false, marketing: false },
            });
        }
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* noop */
    }
    notify();
}

export function setConsent({ action, categories }) {
    const existing = getConsent();
    const consent_id = existing?.consent_id || _genId();
    const cats = { ...(categories || {}), necessary: true };
    const record = {
        consent_id,
        version: CURRENT_POLICY_VERSION,
        decided_at: new Date().toISOString(),
        categories: cats,
    };
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
        /* noop */
    }
    // Fire server-side log without blocking the UI
    void sendLog({
        consent_id,
        version: CURRENT_POLICY_VERSION,
        action,
        categories: cats,
    });
    notify();
    return record;
}

export function openCookiePreferences() {
    notifyOpen();
}

function notify() {
    try {
        window.dispatchEvent(new CustomEvent("elh-consent-change"));
    } catch {
        /* noop */
    }
}

function notifyOpen() {
    try {
        window.dispatchEvent(new CustomEvent("elh-consent-open"));
    } catch {
        /* noop */
    }
}

async function sendLog(body) {
    try {
        await axios.post(`${BACKEND}/api/consent/log`, {
            ...body,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
            locale: typeof navigator !== "undefined" ? navigator.language : "",
        });
    } catch {
        // best-effort; the localStorage record is authoritative client-side
    }
}
